import {IMqttSignalingClient} from "./interfaces/IMqttSignalingClient";
import {filter, Observable, Subject, Subscription, takeUntil} from "rxjs";
import {WebRtcSignalingContent} from "../shared/models/webrtc/web-rtc-signaling";
import {SignalingTransportV2} from "../shared/services/signaling-transport-v2";
import {CallSignalingMessage, SignalingMessageKind} from "../shared/models/webrtc/call/v2/call-signaling-message";
import {CallEntityKind} from "../shared/models/webrtc/call/v2/call-entity";

export class MqttSignalingClientV2 implements IMqttSignalingClient {

    private signalingMessagesSubscription: Subscription | null = null;

    private readonly sdpAnswer$ = new Subject<WebRtcSignalingContent>();
    private readonly sdpOffer$ = new Subject<WebRtcSignalingContent>();
    private readonly iceCandidate$ = new Subject<WebRtcSignalingContent>();
    private closed$ = new Subject<void>();

    constructor(private readonly signalingTransport: SignalingTransportV2,
                private readonly peerId: string,
                private readonly callId: string,
                private readonly sessionId: string,
                private readonly installationId: string,
                private readonly entityId: string,
                private readonly entityKind: CallEntityKind,
                private readonly remoteEntityId: string,
                private readonly remoteEntityKind: CallEntityKind,
                private remotePeerId?: string) {
    }

    open(): void {
        if (this.signalingMessagesSubscription != null) return;

        this.signalingMessagesSubscription = this.signalingTransport.subscribeToSessionMessage(
            this.installationId,
            this.entityId
        ).pipe(
            filter(message => message.session_id === this.sessionId)
        ).subscribe({
            next: message => {
                if (message.kind === SignalingMessageKind.Answer) {
                    this.sdpAnswer$.next(message.content);
                } else if (message.kind === SignalingMessageKind.Offer) {
                    if (!this.remotePeerId)
                        this.remotePeerId = message.peer_id;
                    this.sdpOffer$.next(message.content);
                } else if (message.kind === SignalingMessageKind.Ice) {
                    this.iceCandidate$.next(message.content);
                }
            },
            error: err => console.error("Signaling error: ", err)
        });
    }

    sendOffer(sdpOffer: RTCSessionDescription): void {
        if (!sdpOffer) return;

        const content: WebRtcSignalingContent = {
            type: sdpOffer.type,
            sdp: sdpOffer.sdp
        };

        this.sendSignalingMessage(SignalingMessageKind.Offer, content);
    }

    sendAnswer(sdpAnswer: RTCSessionDescription): void {
        if (!sdpAnswer) return;

        const content: WebRtcSignalingContent = {
            type: sdpAnswer.type,
            sdp: sdpAnswer.sdp
        };

        this.sendSignalingMessage(SignalingMessageKind.Answer, content);
    }

    sendIceCandidate(candidate: RTCIceCandidate): void {
        if (!candidate?.candidate) return;

        const content: WebRtcSignalingContent = {
            sdpMLineIndex: candidate.sdpMLineIndex,
            sdpMid: candidate.sdpMid,
            candidate: candidate.candidate
        };

        this.sendSignalingMessage(SignalingMessageKind.Ice, content);
    }

    onSdpOffer = (): Observable<WebRtcSignalingContent> =>
        this.sdpOffer$.asObservable().pipe(takeUntil(this.closed$));

    onSdpAnswer = (): Observable<WebRtcSignalingContent> =>
        this.sdpAnswer$.asObservable().pipe(
            takeUntil(this.closed$)
        );

    onIceCandidate = (): Observable<WebRtcSignalingContent> =>
        this.iceCandidate$.asObservable().pipe(takeUntil(this.closed$));

    close(): void {
        this.signalingMessagesSubscription?.unsubscribe();
        this.signalingMessagesSubscription = null;
        this.closed$.next();
        this.closed$.complete();
    }

    private sendSignalingMessage(
        kind: SignalingMessageKind,
        content: WebRtcSignalingContent
    ): void {
        const message: CallSignalingMessage = {
            kind,
            content,
            entity: {
                kind: this.entityKind,
                id: this.entityId
            },
            peer_id: this.peerId,
            call_id: this.callId,
            session_id: this.sessionId,
            installation_id: this.installationId
        };

        this.signalingTransport.publishCallSignalingMessage(
            this.installationId,
            this.remoteEntityId,
            this.remoteEntityKind,
            message,
            this.remotePeerId
        );
    }

}
